"use client";
import React, { useState, useEffect } from "react";
import { Typography, Container, Box, Paper, Link } from "@mui/material";
import { Plus_Jakarta_Sans, Inter } from "next/font/google";
import { useTranslation } from "react-i18next";

const PlusJakartaSans = Plus_Jakarta_Sans({
  subsets: ["latin"],
  weight: ["300", "400", "500", "600", "700"],
});

const inter = Inter({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700"],
});

const TableOfContents = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const [activeId, setActiveId] = useState("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { threshold: 0.5 },
    );
    document.querySelectorAll("h1, h2, h3, h4").forEach((heading) => {
      observer.observe(heading);
    });
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  const tocItems = [
    { id: "overview", titleKey: "articleTwoContent.toc.overview", indent: 0 },
    { id: "signs", titleKey: "articleTwoContent.toc.signs", indent: 0 },
    { id: "bias", titleKey: "articleTwoContent.toc.bias", indent: 1 },
    {
      id: "limited-resources",
      titleKey: "articleTwoContent.toc.limitedResources",
      indent: 1,
    },
    { id: "screening", titleKey: "articleTwoContent.toc.screening", indent: 1 },
    {
      id: "cost-per-hire",
      titleKey: "articleTwoContent.toc.costPerHire",
      indent: 1,
    },
    {
      id: "top-talent",
      titleKey: "articleTwoContent.toc.topTalent",
      indent: 1,
    },
    {
      id: "time-to-fill",
      titleKey: "articleTwoContent.toc.timeToFill",
      indent: 1,
    },
    {
      id: "mundane-tasks",
      titleKey: "articleTwoContent.toc.mundaneTasks",
      indent: 1,
    },
    { id: "pipeline", titleKey: "articleTwoContent.toc.pipeline", indent: 1 },
    { id: "no-tool", titleKey: "articleTwoContent.toc.noTool", indent: 1 },
    { id: "ready", titleKey: "articleTwoContent.toc.ready", indent: 0 },
    {
      id: "conclusion",
      titleKey: "articleTwoContent.toc.conclusion",
      indent: 0,
    },
  ];

  return (
    <Paper
      elevation={3}
      sx={{
        position: "fixed",
        right: isArabic ? "auto" : { xs: "5%", sm: "10%" },
        left: isArabic ? { xs: "5%", sm: "10%" } : "auto",
        top: { xs: "50%", sm: "66.6%" },
        display: { xs: "none", sm: "none", md: "none", xl: "none" },
        transform: "translateY(-50%)",
        width: "280px",
        p: 2,
        maxHeight: "calc(80vh - 20%)",
        overflowY: "auto",
        scrollBehavior: "smooth",
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          fontFamily: PlusJakartaSans.style.fontFamily,
          fontWeight: "700",
          textAlign: isArabic ? "right" : "left",
        }}
      >
        {t("articleTwoContent.toc.title")}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {tocItems.map(({ id, titleKey, indent }) => (
          <Typography
            key={id}
            onClick={() => scrollToSection(id)}
            sx={{
              cursor: "pointer",
              pl: isArabic ? 0 : indent * 2,
              pr: isArabic ? indent * 2 : 0,
              color: activeId === id ? "primary.main" : "#4B5963",
              fontFamily: inter.style.fontFamily,
              fontSize: "14px",
              textAlign: isArabic ? "right" : "left",
              "&:hover": { color: "primary.main" },
            }}
          >
            {t(titleKey)}
          </Typography>
        ))}
      </Box>
    </Paper>
  );
};

const ArticleTwoContent = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const paragraphSx = {
    mb: ".75%",
    fontFamily: inter.style.fontFamily,
    fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
    color: "#4B5963",
    fontWeight: "400",
    textAlign: isArabic ? "right" : "left",
    direction: isArabic ? "rtl" : "ltr",
  };

  const headingSx = {
    fontFamily: PlusJakartaSans.style.fontFamily,
    fontWeight: "700",
    fontSize: { xs: "24px", sm: "28px", md: "34px" },
    textAlign: isArabic ? "right" : "left",
    direction: isArabic ? "rtl" : "ltr",
  };

  return (
    <Container maxWidth="xl" sx={{ direction: isArabic ? "rtl" : "ltr" }}>
      <TableOfContents />
      <Paper elevation={2} sx={{ my: 4, border: "none", boxShadow: "none" }}>
        {/* Overview */}
        <Typography
          id="overview"
          variant="h4"
          component="h1"
          gutterBottom
          sx={headingSx}
        >
          {t("articleTwoContent.overview.heading")}
        </Typography>
        <Typography sx={paragraphSx}>
          {t("articleTwoContent.overview.p1")}
        </Typography>
        <Typography sx={paragraphSx}>
          {t("articleTwoContent.overview.p2")}
        </Typography>
        <Typography sx={paragraphSx}>
          {t("articleTwoContent.overview.p3")}
        </Typography>
        <Typography sx={paragraphSx}>
          {t("articleTwoContent.overview.p4")}
        </Typography>
        <Typography sx={paragraphSx}>
          {t("articleTwoContent.overview.p5")}
        </Typography>

        {/* Signs heading */}
        <Typography
          id="signs"
          variant="h4"
          component="h2"
          gutterBottom
          sx={{ ...headingSx, mt: 4 }}
        >
          {t("articleTwoContent.signs.heading")}
        </Typography>

        {/* Bias */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="bias"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ ...headingSx, mt: 2 }}
          >
            {t("articleTwoContent.bias.heading")}
          </Typography>
          <Typography sx={{ ...paragraphSx, mt: "1%" }}>
            {t("articleTwoContent.bias.p1")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.bias.p2")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.bias.p3")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.bias.p4")}
          </Typography>
        </Box>

        {/* Limited Resources */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="limited-resources"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ ...headingSx, mt: 4 }}
          >
            {t("articleTwoContent.limitedResources.heading")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.limitedResources.p1")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.limitedResources.p2")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.limitedResources.p3")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.limitedResources.p4")}
          </Typography>
        </Box>

        {/* Screening */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="screening"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ ...headingSx, mt: 4 }}
          >
            {t("articleTwoContent.screening.heading")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.screening.p1")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.screening.p2")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.screening.p3")}
          </Typography>
        </Box>

        {/* Cost Per Hire */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="cost-per-hire"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ ...headingSx, mt: 4 }}
          >
            {t("articleTwoContent.costPerHire.heading")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.costPerHire.p1start")}
            <Link
              href="https://www.zippia.com/advice/cost-of-hiring-statistics-average-cost-per-hire/"
              target="_blank"
              sx={{ color: "#9048FB", textDecoration: "none" }}
            >
              {t("articleTwoContent.costPerHire.p1link")}
            </Link>
            {t("articleTwoContent.costPerHire.p1end")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.costPerHire.p2")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.costPerHire.p3")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.costPerHire.p4")}
          </Typography>
        </Box>

        {/* Top Talent */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="top-talent"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ ...headingSx, mt: 4 }}
          >
            {t("articleTwoContent.topTalent.heading")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.topTalent.p1")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.topTalent.p2")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.topTalent.p3")}
          </Typography>
        </Box>

        {/* Time To Fill */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="time-to-fill"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ ...headingSx, mt: 4 }}
          >
            {t("articleTwoContent.timeToFill.heading")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.timeToFill.p1start")}
            <Link
              href="https://gitnux.org/"
              target="_blank"
              sx={{ color: "#9048FB", textDecoration: "none" }}
            >
              {t("articleTwoContent.timeToFill.p1link")}
            </Link>
            {t("articleTwoContent.timeToFill.p1end")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.timeToFill.p2")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.timeToFill.p3")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.timeToFill.p4")}
          </Typography>
        </Box>

        {/* Mundane Tasks */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="mundane-tasks"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ ...headingSx, mt: 4 }}
          >
            {t("articleTwoContent.mundaneTasks.heading")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.mundaneTasks.p1")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.mundaneTasks.p2")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.mundaneTasks.p3")}
          </Typography>
        </Box>

        {/* Pipeline */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="pipeline"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ ...headingSx, mt: 4 }}
          >
            {t("articleTwoContent.pipeline.heading")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.pipeline.p1")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.pipeline.p2")}
          </Typography>
        </Box>

        {/* No Tool */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="no-tool"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ ...headingSx, mt: 4 }}
          >
            {t("articleTwoContent.noTool.heading")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.noTool.p1")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.noTool.p2")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.noTool.p3")}
          </Typography>
        </Box>

        {/* Ready */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="ready"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ ...headingSx, mt: 4 }}
          >
            {t("articleTwoContent.ready.heading")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.ready.p1")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.ready.p2")}
          </Typography>
        </Box>

        {/* Conclusion */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="conclusion"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ ...headingSx, mt: 4 }}
          >
            {t("articleTwoContent.conclusion.heading")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.conclusion.p1")}
          </Typography>
          <Typography sx={paragraphSx}>
            {t("articleTwoContent.conclusion.p2")}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default ArticleTwoContent;
